import React, { createContext, memo, useCallback, useContext, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { ReshipModal } from './reship-modal';
import { useModal } from '../../hooks/use-modal';
import { orderDetailsReshipSuccess } from '../../store/orders/order-details/orders.details.actions';
import { restoreFulfillmentsSuccess, silentRequestGetOrders } from '../../store/orders/orders.actions';
import { restoreSingleShipment } from '../../store/orders/orders.api';
import Global from '../../store/global-selectors';
import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import { messages } from './messages';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import { ChildrenPropType } from '../../prop-types/children';
import { useEntitiesFulfillmentPlanProperty } from '../../hooks/entities/selectors/fulfillment-plans';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../modals/order-details-modal/event-tracking/use-track-order-details-action';
import { RESHIP_SHIPMENT_ACTIONS, useTrackReshipShipmentAction } from './use-track-reship-action';
import { noop } from 'ui-components/default-props/noop';

const initialValue = {
  reship: noop,
  canReship: false,
};

const ReshipContext = createContext(initialValue);

export const useReshipContext = () => useContext(ReshipContext);

export const ReshipContextProvider = memo(({ salesOrderId, fulfillmentPlanId, children, isImportedAsShipped }) => {
  const userHasShippingRole = useSelector(Global.selectUserhasShippingRole);
  const orderGridStatus = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId, 'orderGridStatus');

  const { isOpen, open, close } = useModal();

  const dispatch = useDispatch();

  const trackOrderDetailsAction = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.RESHIP_SHIPMENT);

  const trackReshipShipmentClick = useTrackReshipShipmentAction(RESHIP_SHIPMENT_ACTIONS.START, fulfillmentPlanId);
  const trackReshipShipmentClose = useTrackReshipShipmentAction(RESHIP_SHIPMENT_ACTIONS.CLOSE, fulfillmentPlanId);
  const trackReshipShipmentSuccess = useTrackReshipShipmentAction(RESHIP_SHIPMENT_ACTIONS.SUCCESS, fulfillmentPlanId);
  const trackReshipShipmentError = useTrackReshipShipmentAction(RESHIP_SHIPMENT_ACTIONS.ERROR, fulfillmentPlanId);

  const onReshipSuccess = useCallback(
    (newFulfillmentPlanId) => {
      const trackData = { reshipFulfillmentPlanId: newFulfillmentPlanId };
      trackReshipShipmentSuccess(trackData);
      dispatch(
        orderDetailsReshipSuccess({
          salesOrderId,
          newFulfillmentPlanId,
        }),
      );
      dispatch(silentRequestGetOrders());
      close();
      trackReshipShipmentClose(trackData);
    },
    [close, dispatch, salesOrderId, trackReshipShipmentClose, trackReshipShipmentSuccess],
  );

  const onReshipClose = useCallback(() => {
    close();
    trackReshipShipmentClose();
  }, [close, trackReshipShipmentClose]);

  const reshipImportedAsShipped = useCallback(() => {
    restoreSingleShipment(fulfillmentPlanId)
      .then(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.reshipSuccessNotificationTitle} />,
          content: <FormattedMessage {...messages.reshipSuccessNotificationContent} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        dispatch(restoreFulfillmentsSuccess(fulfillmentPlanId));
        trackReshipShipmentSuccess({
          reshipFulfillmentPlanId: fulfillmentPlanId,
        });
      })
      .catch((error) => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.reshipErrorNotificationTitle} />,
          content: <FormattedMessage {...messages.reshipErrorNotificationContent} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        trackReshipShipmentError({ error });
      });
  }, [dispatch, fulfillmentPlanId, trackReshipShipmentError, trackReshipShipmentSuccess]);

  const reship = useCallback(() => {
    trackOrderDetailsAction();
    trackReshipShipmentClick();
    if (isImportedAsShipped) {
      reshipImportedAsShipped();
    } else {
      open();
    }
  }, [isImportedAsShipped, open, reshipImportedAsShipped, trackOrderDetailsAction, trackReshipShipmentClick]);

  const value = useMemo(() => {
    return {
      reship,
      canReship: userHasShippingRole && (orderGridStatus === FulfillmentPlanOrderGridStatus.Shipped.name || isImportedAsShipped),
      trackReshipShipmentError,
    };
  }, [isImportedAsShipped, orderGridStatus, reship, trackReshipShipmentError, userHasShippingRole]);

  return (
    <ReshipContext.Provider value={value}>
      {children}
      {isOpen && (
        <ReshipModal
          fulfillmentPlanId={fulfillmentPlanId}
          onClose={onReshipClose}
          onError={trackReshipShipmentError}
          onSuccess={onReshipSuccess}
          salesOrderId={salesOrderId}
        />
      )}
    </ReshipContext.Provider>
  );
});

ReshipContextProvider.propTypes = {
  salesOrderId: PropTypes.string.isRequired,
  fulfillmentPlanId: PropTypes.string.isRequired,
  isImportedAsShipped: PropTypes.bool.isRequired,
  children: ChildrenPropType.isRequired,
};
